// Hand-written GraphQL operation documents for the admin SPA.
//
// Each document is named after its root field (e.g. `query clients`, `mutation createApiKey`)
// so MSW's operation-name matching (graphql.query('clients', ...)) lines up with the mocks in
// ./mocks.ts and the live Absinthe resolvers. Result/variable shapes are typed from the
// generated ./types.ts so the contract is the single source of truth — we never redefine the
// domain types here, only slice the Query/Mutation fields we select.

import type {
  ApiKey,
  Application,
  AuditLogEntryConnection,
  CanonicalModel,
  CanonicalModelKind,
  Client,
  ClientQuota,
  ClientQuotaReportRow,
  CreateApiKeyInput,
  CreateApiKeyResult,
  CreateApplicationInput,
  CreateCanonicalModelInput,
  CreateClientInput,
  ModelAlias,
  OverrideMode,
  Provider,
  ProviderHealth,
  ProviderProbeResult,
  ProviderType,
  Quota,
  RefreshModelsSummary,
  RouteCandidate,
  RoutedReason,
  RouteSimulation,
  RoutingPolicy,
  UpdateCanonicalModelInput,
  UpsertClientQuotaInput,
  UpsertModelAliasInput,
  UpsertProviderInput,
  UpsertQuotaInput,
  UpsertRoutingPolicyInput,
  UsageFilter,
  UsageRecord,
  UsageRecordConnection,
  UsageSummary,
} from './types';

// ---------------------------------------------------------------------------
// Reusable fragments (string concatenation keeps this dependency-free)
// ---------------------------------------------------------------------------

const API_KEY_FIELDS = `
  id
  keyPrefix
  maskedKey
  scopes
  overrideMode
  pinnedProvider
  status
  lastUsedAt
  insertedAt
`;

const APPLICATION_FIELDS = `
  id
  name
  environment
  insertedAt
`;

/**
 * The result shape of the `APPLICATION_FIELDS` list fragment — EXACTLY the four scalar fields it
 * selects, and nothing more. LIST/CREATE contexts (`clients`/`client`/`createApplication`) embed
 * this fragment WITHOUT selecting `client { … }` or `apiKeys { … }`, so the server returns neither.
 *
 * Typing those results as the full generated `Application` (which has non-null `apiKeys`/`client`)
 * would let a consumer read fields that are `undefined` at runtime — the latent trap FIX-17 closes.
 * Only `application(id:)` (the DETAIL query) additionally selects `client`/`apiKeys`, so that one
 * alone stays typed as the full `Application`.
 *
 * Field types are sliced directly from the generated `Application` so this stays in lockstep with
 * the frozen contract — we never restate the scalar/enum shapes by hand.
 */
export type ApplicationSummary = Pick<
  Application,
  '__typename' | 'id' | 'name' | 'environment' | 'insertedAt'
>;

/** A `Client` whose `applications` are list-fragment summaries (no `apiKeys`/`client` back-refs). */
export type ClientSummary = Omit<Client, 'applications'> & {
  applications: Array<ApplicationSummary>;
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** `clients` — top-level tenant list with their applications (for the browse tree). */
/**
 * Single-round-trip query for the quota form's scope-id picker. Returns every client +
 * application + API key the operator can scope a quota against. Trimmed to ONLY the
 * fields the picker needs (id + display label), so the payload stays small.
 */
export const QUOTA_SCOPE_OPTIONS_QUERY = `
  query quotaScopeOptions {
    clients {
      id
      name
      applications {
        id
        name
        apiKeys {
          id
          keyPrefix
          status
        }
      }
    }
  }
`;

export interface QuotaScopeOptionsResult {
  clients: Array<{
    id: string;
    name: string;
    applications: Array<{
      id: string;
      name: string;
      apiKeys: Array<{
        id: string;
        keyPrefix: string;
        status: string;
      }>;
    }>;
  }>;
}

export const CLIENTS_QUERY = `
  query clients {
    clients {
      id
      name
      status
      dataResidency
      insertedAt
      updatedAt
      applications {
        ${APPLICATION_FIELDS}
      }
    }
  }
`;

export interface ClientsQueryResult {
  clients: ClientSummary[];
}

/** `client` — a single tenant with applications, for the client detail view. */
export const CLIENT_QUERY = `
  query client($id: ID!) {
    client(id: $id) {
      id
      name
      status
      dataResidency
      insertedAt
      updatedAt
      applications {
        ${APPLICATION_FIELDS}
      }
    }
  }
`;

export interface ClientQueryResult {
  client: ClientSummary | null;
}

export interface ClientQueryVariables {
  id: string;
}

/** `application` — a single app with its API keys, for the application detail view. */
export const APPLICATION_QUERY = `
  query application($id: ID!) {
    application(id: $id) {
      ${APPLICATION_FIELDS}
      client {
        id
        name
        status
      }
      apiKeys {
        ${API_KEY_FIELDS}
      }
    }
  }
`;

export interface ApplicationQueryResult {
  application: Application | null;
}

export interface ApplicationQueryVariables {
  id: string;
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export const CREATE_CLIENT_MUTATION = `
  mutation createClient($input: CreateClientInput!) {
    createClient(input: $input) {
      id
      name
      status
      dataResidency
      insertedAt
      updatedAt
    }
  }
`;

export interface CreateClientMutationResult {
  createClient: Client;
}

export interface CreateClientMutationVariables {
  input: CreateClientInput;
}

export const CREATE_APPLICATION_MUTATION = `
  mutation createApplication($input: CreateApplicationInput!) {
    createApplication(input: $input) {
      ${APPLICATION_FIELDS}
    }
  }
`;

export interface CreateApplicationMutationResult {
  createApplication: ApplicationSummary;
}

export interface CreateApplicationMutationVariables {
  input: CreateApplicationInput;
}

/** `createApiKey` — the ONLY operation (with rotateApiKey) that returns the raw secret. */
export const CREATE_API_KEY_MUTATION = `
  mutation createApiKey($input: CreateApiKeyInput!) {
    createApiKey(input: $input) {
      rawKey
      apiKey {
        ${API_KEY_FIELDS}
      }
    }
  }
`;

export interface CreateApiKeyMutationResult {
  createApiKey: CreateApiKeyResult;
}

export interface CreateApiKeyMutationVariables {
  input: CreateApiKeyInput;
}

export const REVOKE_API_KEY_MUTATION = `
  mutation revokeApiKey($id: ID!) {
    revokeApiKey(id: $id) {
      ${API_KEY_FIELDS}
    }
  }
`;

export interface RevokeApiKeyMutationResult {
  revokeApiKey: ApiKey;
}

export interface RevokeApiKeyMutationVariables {
  id: string;
}

/** `rotateApiKey` — issues a replacement secret; the new rawKey is surfaced once. */
export const ROTATE_API_KEY_MUTATION = `
  mutation rotateApiKey($id: ID!) {
    rotateApiKey(id: $id) {
      rawKey
      apiKey {
        ${API_KEY_FIELDS}
      }
    }
  }
`;

export interface RotateApiKeyMutationResult {
  rotateApiKey: CreateApiKeyResult;
}

export interface RotateApiKeyMutationVariables {
  id: string;
}

export const SET_API_KEY_OVERRIDE_MUTATION = `
  mutation setApiKeyOverride($id: ID!, $overrideMode: OverrideMode!, $pinnedProvider: ProviderType) {
    setApiKeyOverride(id: $id, overrideMode: $overrideMode, pinnedProvider: $pinnedProvider) {
      ${API_KEY_FIELDS}
    }
  }
`;

export interface SetApiKeyOverrideMutationResult {
  setApiKeyOverride: ApiKey;
}

export interface SetApiKeyOverrideMutationVariables {
  id: string;
  overrideMode: OverrideMode;
  pinnedProvider?: ProviderType | null | undefined;
}

// ===========================================================================
// Providers · routing policies · quotas (Chunk 28 — E8)
// ===========================================================================

// ---------------------------------------------------------------------------
// Provider health board (US-8.2.1)
// ---------------------------------------------------------------------------

/** Selected on every `Provider` we render on the health board. credentialsRef is NEVER selected. */
const PROVIDER_FIELDS = `
  id
  type
  displayName
  baseUrl
  credentialsRef
  enabled
  weight
  maxConcurrency
  dataResidency
  probePath
  recentHealthEvents {
    at
    source
    outcome
    url
    reason
    latencyMs
    statusCode
  }
  defaultCanonicalModel {
    id
    name
    kind
    enabled
  }
  health {
    state
    inFlight
    consecutiveFailures
    lastProbeAt
  }
  archivedAt
  metrics24h {
    requestCount
    errorCount
    errorRate
    avgLatencyMs
    lastUsedAt
  }
  modelAliases {
    id
    canonicalModel
    providerModel
    enabled
    wildcard
  }
`;

/** `providers` — the full provider fleet with current (query-time) health for the board. */
export const PROVIDERS_QUERY = `
  query providers($includeArchived: Boolean = false) {
    providers(includeArchived: $includeArchived) {
      ${PROVIDER_FIELDS}
    }
  }
`;

export interface ProvidersQueryResult {
  providers: Provider[];
}

export interface ProvidersQueryVariables {
  includeArchived?: boolean;
}

export const ARCHIVE_PROVIDER_MUTATION = `
  mutation archiveProvider($id: ID!) {
    archiveProvider(id: $id) {
      ${PROVIDER_FIELDS}
    }
  }
`;

export const UNARCHIVE_PROVIDER_MUTATION = `
  mutation unarchiveProvider($id: ID!) {
    unarchiveProvider(id: $id) {
      ${PROVIDER_FIELDS}
    }
  }
`;

export interface ArchiveProviderResult {
  archiveProvider: Provider;
}

export interface UnarchiveProviderResult {
  unarchiveProvider: Provider;
}

export interface ArchiveProviderVariables {
  id: string;
}

// 2026-07-05 — "Browse upstream models" nested dialog on the provider edit modal.
// Reads the ModelListCache-backed view of what the upstream is currently serving,
// cross-referenced against the provider's own enabled aliases. Cache-only: if cold
// (no successful probe yet), `models` is `[]` and `probedAt` is null, at which point
// the SPA fires `probeProvider(id)` to warm it and re-queries.
export const PROVIDER_UPSTREAM_MODELS_QUERY = `
  query providerUpstreamModels($id: ID!) {
    providerUpstreamModels(id: $id) {
      probedAt
      provider {
        id
        displayName
        type
      }
      models {
        id
        aliasedAs
      }
    }
  }
`;

export interface ProviderUpstreamModelsResult {
  providerUpstreamModels: {
    __typename?: 'ProviderUpstreamModels';
    probedAt: string | null;
    provider: {
      __typename?: 'Provider';
      id: string;
      displayName: string;
      type: string;
    };
    models: Array<{
      __typename?: 'UpstreamModel';
      id: string;
      aliasedAs: string[];
    }>;
  };
}

export interface ProviderUpstreamModelsVariables {
  id: string;
}

/** `setProviderEnabled` — toggle a provider in/out of routing. Returns the updated Provider. */
export const SET_PROVIDER_ENABLED_MUTATION = `
  mutation setProviderEnabled($id: ID!, $enabled: Boolean!) {
    setProviderEnabled(id: $id, enabled: $enabled) {
      ${PROVIDER_FIELDS}
    }
  }
`;

export interface SetProviderEnabledMutationResult {
  setProviderEnabled: Provider;
}

export interface SetProviderEnabledMutationVariables {
  id: string;
  enabled: boolean;
}

/** `upsertProvider` — create (no id) or update (with id) a provider. Returns the updated Provider. */
export const UPSERT_PROVIDER_MUTATION = `
  mutation upsertProvider($input: UpsertProviderInput!) {
    upsertProvider(input: $input) {
      ${PROVIDER_FIELDS}
    }
  }
`;

export interface UpsertProviderMutationResult {
  upsertProvider: Provider;
}

export interface UpsertProviderMutationVariables {
  input: UpsertProviderInput;
}

// ---------------------------------------------------------------------------
// Provider test-connection probe (W3b)
//
// `ProbeStatus` + `ProviderProbeResult` now exist in the regenerated ./types.ts (probeProvider
// contract amendment), so we re-export the codegen types here rather than hand-writing them — the
// contract stays the single source of truth. The probe selection takes the full `Provider` via
// PROVIDER_FIELDS, matching the codegen `ProviderProbeResult.provider` shape exactly, so no
// narrowing is needed.
// ---------------------------------------------------------------------------

// Re-exported so probe consumers (useProbeProvider, ProbeResultBadge) keep importing from
// ./operations as their operation barrel. `ProbeStatus` is a codegen enum (value + type);
// `ProviderProbeResult` is also imported above for use in this module's result interfaces.
export { ProbeStatus } from './types';
export type { ProviderProbeResult };

/** `probeProvider` — actively test an upstream provider's connectivity/credentials. */
export const PROBE_PROVIDER_MUTATION = `
  mutation probeProvider($id: ID!) {
    probeProvider(id: $id) {
      status
      latencyMs
      message
      checkedAt
      provider {
        ${PROVIDER_FIELDS}
      }
    }
  }
`;

export interface ProbeProviderMutationResult {
  probeProvider: ProviderProbeResult;
}

export interface ProbeProviderMutationVariables {
  id: string;
}

/**
 * `resetProviderHealth` — operator action: clear the failure/success counters and force
 * state back to HEALTHY, then run one fresh probe. Returns the updated Provider so the
 * SPA can replace the cached entry without a follow-up refetch.
 */
export const RESET_PROVIDER_HEALTH_MUTATION = `
  mutation resetProviderHealth($id: ID!) {
    resetProviderHealth(id: $id) {
      ${PROVIDER_FIELDS}
    }
  }
`;

export interface ResetProviderHealthMutationResult {
  resetProviderHealth: Provider;
}

export interface ResetProviderHealthMutationVariables {
  id: string;
}

// ---------------------------------------------------------------------------
// Routing policy editor (US-8.3.1)
// ---------------------------------------------------------------------------

/** Selected on every `RoutingPolicy`. `scopeApplication` is resolved to {id,name} for display. */
const ROUTING_POLICY_FIELDS = `
  id
  name
  providerOrder
  capacityThreshold
  scopeApplication {
    id
    name
  }
  scopeCanonicalModel
  enabled
`;

/** `routingPolicies` — every policy, ordered list of provider preference + scope + threshold. */
export const ROUTING_POLICIES_QUERY = `
  query routingPolicies {
    routingPolicies {
      ${ROUTING_POLICY_FIELDS}
    }
  }
`;

export interface RoutingPoliciesQueryResult {
  routingPolicies: RoutingPolicy[];
}

/** `upsertRoutingPolicy` — create (no id) or update (with id) a policy. */
export const UPSERT_ROUTING_POLICY_MUTATION = `
  mutation upsertRoutingPolicy($input: UpsertRoutingPolicyInput!) {
    upsertRoutingPolicy(input: $input) {
      ${ROUTING_POLICY_FIELDS}
    }
  }
`;

export interface UpsertRoutingPolicyMutationResult {
  upsertRoutingPolicy: RoutingPolicy;
}

export interface UpsertRoutingPolicyMutationVariables {
  input: UpsertRoutingPolicyInput;
}

/** `deleteRoutingPolicy` — remove a policy. Returns whether it was deleted. */
export const DELETE_ROUTING_POLICY_MUTATION = `
  mutation deleteRoutingPolicy($id: ID!) {
    deleteRoutingPolicy(id: $id)
  }
`;

export interface DeleteRoutingPolicyMutationResult {
  deleteRoutingPolicy: boolean;
}

export interface DeleteRoutingPolicyMutationVariables {
  id: string;
}

// ---------------------------------------------------------------------------
// Route simulator (W5 — "Test routing")
//
// `RouteSimulation` / `RouteCandidate` / `RouteSimulationStatus` now exist in the regenerated
// ./types.ts (simulateRoute contract amendment). The SIMULATE_ROUTE_QUERY selects a deliberate
// SUBSET of those types (e.g. only {id,type,displayName,enabled,health{state}} of a Provider, and
// {id,name} of the matched policy), so per the repo's ApplicationSummary precedent we DERIVE the
// selection shapes from the codegen types with Pick/Omit instead of restating them by hand. The
// codegen single source of truth is preserved; we never free-stand the domain shapes.
// ---------------------------------------------------------------------------

/**
 * Top-level simulation status — re-exported codegen enum (value + type). Keeps the
 * `SimulateRouteStatus.Ok / NoPolicy / NoEligibleProvider` call sites unchanged.
 */
export { RouteSimulationStatus as SimulateRouteStatus } from './types';

/**
 * The narrowed `Provider` the candidate hop selects: identity + enabled + health.state only
 * (the query does NOT select weight/baseUrl/modelAliases/etc).
 */
type SimulateRouteCandidateProvider = Pick<
  Provider,
  '__typename' | 'id' | 'type' | 'displayName' | 'enabled'
> & {
  health: Pick<ProviderHealth, '__typename' | 'state'>;
};

/**
 * A single candidate hop in the simulated route, eligible or excluded with a reason. Derived from
 * the codegen `RouteCandidate`, narrowing only `provider` to the fields the query selects.
 */
export type SimulateRouteCandidate = Omit<RouteCandidate, 'provider'> & {
  provider: SimulateRouteCandidateProvider;
};

/** The matched policy (id + name only) the simulation resolved against. */
export type SimulateRoutePolicyRef = Pick<RoutingPolicy, '__typename' | 'id' | 'name'>;

/** The selected hop — only its position is selected by the query. */
export type SimulateRouteSelection = Pick<RouteCandidate, '__typename' | 'position'>;

/**
 * Result of a simulateRoute dry-run. Derived from the codegen `RouteSimulation`, narrowing the
 * nested selections to what the query asks for. `routedReason` is kept as the `RoutedReason` enum
 * (the server returns enum values; codegen widens it to a String scalar) so the label map stays
 * exhaustive.
 */
export type SimulateRouteResult = Pick<RouteSimulation, '__typename' | 'status' | 'checkedAt'> & {
  /** null unless a hop was selected (OK). */
  routedReason: RoutedReason | null;
  /** null when no policy matched (NO_POLICY). */
  policy: SimulateRoutePolicyRef | null;
  /** null unless a hop was selected (OK). */
  selected: SimulateRouteSelection | null;
  candidates: SimulateRouteCandidate[];
};

/** `simulateRoute` — dry-run the router for an (optional app, canonical model) pair. */
export const SIMULATE_ROUTE_QUERY = `
  query SimulateRoute($applicationId: ID, $canonicalModel: String!) {
    simulateRoute(applicationId: $applicationId, canonicalModel: $canonicalModel) {
      status
      routedReason
      checkedAt
      policy {
        id
        name
      }
      selected {
        position
      }
      candidates {
        position
        eligible
        exclusionReason
        providerModel
        provider {
          id
          type
          displayName
          enabled
          health {
            state
          }
        }
      }
    }
  }
`;

export interface SimulateRouteQueryResult {
  simulateRoute: SimulateRouteResult;
}

export interface SimulateRouteQueryVariables {
  applicationId?: string | null | undefined;
  canonicalModel: string;
}

// ---------------------------------------------------------------------------
// Quota editor (US-8.4.1)
// ---------------------------------------------------------------------------

const QUOTA_FIELDS = `
  id
  scope
  scopeId
  scopeName
  rpm
  tpm
  dailyTokenCap
  maxConcurrency
`;

/** `quotas` — every quota across scopes (KEY/APP/CLIENT). */
export const QUOTAS_QUERY = `
  query quotas {
    quotas {
      ${QUOTA_FIELDS}
    }
  }
`;

export interface QuotasQueryResult {
  quotas: Quota[];
}

/** `upsertQuota` — create (no id) or update (with id) a quota for a scope+scopeId. */
export const UPSERT_QUOTA_MUTATION = `
  mutation upsertQuota($input: UpsertQuotaInput!) {
    upsertQuota(input: $input) {
      ${QUOTA_FIELDS}
    }
  }
`;

export interface UpsertQuotaMutationResult {
  upsertQuota: Quota;
}

export interface UpsertQuotaMutationVariables {
  input: UpsertQuotaInput;
}

/** `deleteQuota` — remove a quota. Returns whether it was deleted. */
export const DELETE_QUOTA_MUTATION = `
  mutation deleteQuota($id: ID!) {
    deleteQuota(id: $id)
  }
`;

export interface DeleteQuotaMutationResult {
  deleteQuota: boolean;
}

export interface DeleteQuotaMutationVariables {
  id: string;
}

// ---------------------------------------------------------------------------
// Client monthly quotas + report (2026-08-09)
// ---------------------------------------------------------------------------

const CLIENT_QUOTA_FIELDS = `
  id
  clientId
  clientName
  monthlyTokenCap
  monthlyRequestCap
  enforcementMode
`;

/** `clientQuotas` — every configured client monthly quota. */
export const CLIENT_QUOTAS_QUERY = `
  query clientQuotas {
    clientQuotas {
      ${CLIENT_QUOTA_FIELDS}
    }
  }
`;

export interface ClientQuotasQueryResult {
  clientQuotas: ClientQuota[];
}

/** `clientQuotaReport` — per-client usage vs monthly caps for one "YYYY-MM" month. */
export const CLIENT_QUOTA_REPORT_QUERY = `
  query clientQuotaReport($month: String!) {
    clientQuotaReport(month: $month) {
      clientId
      clientName
      month
      requests
      promptTokens
      completionTokens
      reasoningTokens
      totalTokens
      monthlyTokenCap
      monthlyRequestCap
      enforcementMode
      overTokenCap
      overRequestCap
    }
  }
`;

export interface ClientQuotaReportQueryResult {
  clientQuotaReport: ClientQuotaReportRow[];
}

export interface ClientQuotaReportQueryVariables {
  month: string;
}

/** `upsertClientQuota` — create (no id) or update (with id) a client's monthly quota. */
export const UPSERT_CLIENT_QUOTA_MUTATION = `
  mutation upsertClientQuota($input: UpsertClientQuotaInput!) {
    upsertClientQuota(input: $input) {
      ${CLIENT_QUOTA_FIELDS}
    }
  }
`;

export interface UpsertClientQuotaMutationResult {
  upsertClientQuota: ClientQuota;
}

export interface UpsertClientQuotaMutationVariables {
  input: UpsertClientQuotaInput;
}

/** `deleteClientQuota` — remove a client monthly quota. Returns whether it was deleted. */
export const DELETE_CLIENT_QUOTA_MUTATION = `
  mutation deleteClientQuota($id: ID!) {
    deleteClientQuota(id: $id)
  }
`;

export interface DeleteClientQuotaMutationResult {
  deleteClientQuota: boolean;
}

export interface DeleteClientQuotaMutationVariables {
  id: string;
}

// ===========================================================================
// Usage dashboards · audit log · live subscriptions (Chunk 29 — E8)
// ===========================================================================

// ---------------------------------------------------------------------------
// Usage dashboard (US-8.5.1)
// ---------------------------------------------------------------------------

/**
 * Selected on every `UsageRecord` row in the feed. Includes per-hop telemetry snapshots
 * added 2026-06-06 — proxy_request / upstream_request / upstream_response / proxy_response
 * — plus `actualProviderModel` (the upstream-served id, distinct from the requested
 * alias). These columns are nullable and JSON-typed; the SPA Request Details modal renders
 * them in collapsible panels when populated.
 */
const USAGE_RECORD_FIELDS = `
  id
  canonicalModel
  chosenProvider
  providerModel
  actualProviderModel
  promptTokens
  completionTokens
  reasoningTokens
  queueMs
  upstreamMs
  totalMs
  status
  routedReason
  errorCode
  streamed
  costEstimate
  insertedAt
  proxyRequest
  upstreamRequest
  upstreamResponse
  proxyResponse
  policyId
  policyName
  routingDecision
  client {
    id
    name
  }
  application {
    id
    name
  }
  apiKey {
    id
    keyPrefix
  }
`;

/** `usageSummary` — pre-aggregated rollup powering the headline metrics + the byProvider viz. */
export const USAGE_SUMMARY_QUERY = `
  query usageSummary($filter: UsageFilter) {
    usageSummary(filter: $filter) {
      totalRequests
      totalTokens
      totalCostEstimate
      byProvider {
        provider
        requests
        tokens
        failureRate
      }
    }
  }
`;

export interface UsageSummaryQueryResult {
  usageSummary: UsageSummary;
}

// 2026-06-08 — runtime-toggleable admin settings, exposed on the usage page so an
// operator can flip `logContent` without redeploying. Persisted in the
// `system_settings` table and mirrored into Application env on the backend.
export const SYSTEM_SETTINGS_QUERY = `
  query systemSettings {
    systemSettings {
      logContent
    }
  }
`;

export interface SystemSettingsSnapshot {
  __typename?: 'SystemSettings';
  logContent: boolean;
}

export interface SystemSettingsQueryResult {
  systemSettings: SystemSettingsSnapshot;
}

export const SET_LOG_CONTENT_MUTATION = `
  mutation setLogContent($enabled: Boolean!) {
    setLogContent(enabled: $enabled) {
      logContent
    }
  }
`;

export interface SetLogContentResult {
  setLogContent: SystemSettingsSnapshot;
}

export interface SetLogContentVariables {
  enabled: boolean;
}

export interface UsageSummaryQueryVariables {
  filter?: UsageFilter | null | undefined;
}

/** `usageRecords` — cursor-paginated request feed (filterable). */
export const USAGE_RECORDS_QUERY = `
  query usageRecords($filter: UsageFilter, $first: Int = 50, $after: String) {
    usageRecords(filter: $filter, first: $first, after: $after) {
      edges {
        cursor
        node {
          ${USAGE_RECORD_FIELDS}
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      totalCount
    }
  }
`;

export interface UsageRecordsQueryResult {
  usageRecords: UsageRecordConnection;
}

export interface UsageRecordsQueryVariables {
  filter?: UsageFilter | null | undefined;
  first?: number | undefined;
  after?: string | null | undefined;
}

// ---------------------------------------------------------------------------
// Audit log (US-8.6.1)
// ---------------------------------------------------------------------------

const AUDIT_LOG_ENTRY_FIELDS = `
  id
  actor
  action
  targetType
  targetId
  changes
  insertedAt
`;

/**
 * `auditLog` — keyset-cursor `AuditLogEntryConnection` (NO server-side filter; filter
 * client-side over the fetched page). Pagination is now honest (FIX-18): `after` advances past
 * the prior `endCursor` instead of being ignored, so cursor "load more" no longer duplicates the
 * newest page.
 */
export const AUDIT_LOG_QUERY = `
  query auditLog($first: Int = 50, $after: String) {
    auditLog(first: $first, after: $after) {
      edges {
        cursor
        node {
          ${AUDIT_LOG_ENTRY_FIELDS}
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      totalCount
    }
  }
`;

export interface AuditLogQueryResult {
  auditLog: AuditLogEntryConnection;
}

export interface AuditLogQueryVariables {
  first?: number | undefined;
  after?: string | null | undefined;
}

// ---------------------------------------------------------------------------
// Live subscriptions (US-8.5.1 / US-8.2.1 live)
// ---------------------------------------------------------------------------

/** `usageRecorded` — emitted as each request completes; prepended live into the feed. */
export const USAGE_RECORDED_SUBSCRIPTION = `
  subscription usageRecorded($filter: UsageFilter) {
    usageRecorded(filter: $filter) {
      ${USAGE_RECORD_FIELDS}
    }
  }
`;

export interface UsageRecordedSubscriptionResult {
  usageRecorded: UsageRecord;
}

export interface UsageRecordedSubscriptionVariables {
  filter?: UsageFilter | null | undefined;
}

/**
 * `systemHeartbeat` — server-driven 5s liveness tick that flows through the same
 * subscription pipeline as real data. The SPA tracks the most recent tick and flips the
 * live indicator to STALE when no tick arrives within ~12s (two missed intervals).
 */
export const SYSTEM_HEARTBEAT_SUBSCRIPTION = `
  subscription systemHeartbeat {
    systemHeartbeat {
      at
    }
  }
`;

export interface SystemHeartbeatSubscriptionResult {
  systemHeartbeat: { at: string };
}

/** `providerHealthChanged` — emitted on any provider health transition; upserts the board. */
export const PROVIDER_HEALTH_CHANGED_SUBSCRIPTION = `
  subscription providerHealthChanged {
    providerHealthChanged {
      ${PROVIDER_FIELDS}
    }
  }
`;

export interface ProviderHealthChangedSubscriptionResult {
  providerHealthChanged: Provider;
}

// ===========================================================================
// Docs — live "models & providers" availability panel (Ticket W4, Surface 2)
// ===========================================================================

/**
 * `docsProviders` — the provider fleet WITH each provider's `modelAliases`, powering the
 * in-SPA Docs availability panel (per provider: display name, type, residency, enabled, live
 * health, and the canonical-model → provider-model alias table). It selects the SAME contract
 * fields as the health board's `PROVIDER_FIELDS` (including `modelAliases`), but is a distinct
 * named operation so the docs panel owns its own fetch + MSW handler without touching the health
 * board's. Result is typed as the generated `Provider[]` — the contract stays the source of truth.
 */
export const DOCS_PROVIDERS_QUERY = `
  query docsProviders {
    providers {
      ${PROVIDER_FIELDS}
    }
  }
`;

export interface DocsProvidersQueryResult {
  providers: Provider[];
}

// ── Canonical models (W6) ─────────────────────────────────────────────────

const CANONICAL_MODEL_FIELDS = `
  id
  name
  kind
  displayName
  description
  enabled
  providerCount
  insertedAt
  updatedAt
`;

export const CANONICAL_MODELS_QUERY = `
  query canonicalModels($kind: CanonicalModelKind, $enabled: Boolean) {
    canonicalModels(kind: $kind, enabled: $enabled) {
      ${CANONICAL_MODEL_FIELDS}
    }
  }
`;

export interface CanonicalModelsQueryResult {
  canonicalModels: CanonicalModel[];
}

export interface CanonicalModelsQueryVariables {
  kind?: CanonicalModelKind;
  enabled?: boolean;
}

export const CREATE_CANONICAL_MODEL_MUTATION = `
  mutation createCanonicalModel($input: CreateCanonicalModelInput!) {
    createCanonicalModel(input: $input) {
      ${CANONICAL_MODEL_FIELDS}
    }
  }
`;

export interface CreateCanonicalModelMutationResult {
  createCanonicalModel: CanonicalModel;
}

export interface CreateCanonicalModelMutationVariables {
  input: CreateCanonicalModelInput;
}

export const UPDATE_CANONICAL_MODEL_MUTATION = `
  mutation updateCanonicalModel($input: UpdateCanonicalModelInput!) {
    updateCanonicalModel(input: $input) {
      ${CANONICAL_MODEL_FIELDS}
    }
  }
`;

export interface UpdateCanonicalModelMutationResult {
  updateCanonicalModel: CanonicalModel;
}

export interface UpdateCanonicalModelMutationVariables {
  input: UpdateCanonicalModelInput;
}

export const DELETE_CANONICAL_MODEL_MUTATION = `
  mutation deleteCanonicalModel($id: ID!) {
    deleteCanonicalModel(id: $id)
  }
`;

export interface DeleteCanonicalModelMutationResult {
  deleteCanonicalModel: boolean;
}

export interface DeleteCanonicalModelMutationVariables {
  id: string;
}

// ---------------------------------------------------------------------------
// Model alias CRUD (2026-08-21) — the provider-form alias editor
// ---------------------------------------------------------------------------

const MODEL_ALIAS_FIELDS = `
  id
  canonicalModel
  providerModel
  enabled
  wildcard
`;

/** `upsertModelAlias` — create (no id) or remap/toggle (with id) one provider model alias. */
export const UPSERT_MODEL_ALIAS_MUTATION = `
  mutation upsertModelAlias($input: UpsertModelAliasInput!) {
    upsertModelAlias(input: $input) {
      ${MODEL_ALIAS_FIELDS}
    }
  }
`;

/** The alias slice the editor works with (PROVIDER_FIELDS selects the same five). */
export type ModelAliasRow = Pick<
  ModelAlias,
  'id' | 'canonicalModel' | 'providerModel' | 'enabled' | 'wildcard'
>;

export interface UpsertModelAliasMutationResult {
  upsertModelAlias: ModelAliasRow;
}

export interface UpsertModelAliasMutationVariables {
  input: UpsertModelAliasInput;
}

/** `deleteModelAlias` — remove an alias. Returns whether it was deleted. */
export const DELETE_MODEL_ALIAS_MUTATION = `
  mutation deleteModelAlias($id: ID!) {
    deleteModelAlias(id: $id)
  }
`;

export interface DeleteModelAliasMutationResult {
  deleteModelAlias: boolean;
}

export interface DeleteModelAliasMutationVariables {
  id: string;
}

export const REFRESH_PROVIDER_MODELS_MUTATION = `
  mutation refreshProviderModels($id: ID!) {
    refreshProviderModels(id: $id) {
      added
      existing
      aliased
      unchanged
    }
  }
`;

export interface RefreshProviderModelsMutationResult {
  refreshProviderModels: RefreshModelsSummary;
}

export interface RefreshProviderModelsMutationVariables {
  id: string;
}

export const SET_PROVIDER_DEFAULT_MODEL_MUTATION = `
  mutation setProviderDefaultModel($id: ID!, $canonicalModelId: ID) {
    setProviderDefaultModel(id: $id, canonicalModelId: $canonicalModelId) {
      id
      defaultCanonicalModel {
        id
        name
      }
    }
  }
`;

export interface SetProviderDefaultModelMutationResult {
  setProviderDefaultModel: {
    id: string;
    defaultCanonicalModel: { id: string; name: string } | null;
  };
}

export interface SetProviderDefaultModelMutationVariables {
  id: string;
  canonicalModelId: string | null;
}
